import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import ArticleForm from '../components/forms/ArticleForm';
import { getArticleById, updateArticle } from '../services/ArticleService';
import { deleteImages } from '../services/UploadService';
import { IArticle } from '../types/Article';
import styles from '../styles/FormPages.module.css';
import { useToast } from '../hooks/useToast'; // ✅ Añadido

const DEFAULT_PLACEHOLDER_IMAGE = 'https://via.placeholder.com/1200x800.png?text=Art%C3%ADculo+Paso';

const EditArticle: React.FC = () => {
    const { id } = useParams<{ id: string }>(); 
    const navigate = useNavigate();
    const [article, setArticle] = useState<IArticle | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasSaved = useRef(false);
    const sessionUploads = useRef<string[]>([]);
    const toast = useToast(); // ✅ Añadido

    useEffect(() => {
        const uploadsToClean = sessionUploads;
        const cleanupFunction = deleteImages;
        return () => {
            if (!hasSaved.current && uploadsToClean.current.length > 0) {
                console.log("Cancelando edición, limpiando imágenes:", uploadsToClean.current);
                cleanupFunction(uploadsToClean.current);
            }
        };
    }, []);

    useEffect(() => {
        setLoading(true);
        setError(null);
        const articleId = id ? parseInt(id, 10) : undefined;
        
        const fetchArticle = async () => {
            if (articleId !== undefined && !isNaN(articleId)) {
                try {
                    const fetchedArticle = await getArticleById(articleId); 
                    setArticle(fetchedArticle);
                } catch (err: any) { 
                    setError(err.message);
                    toast.error('Error', 'No se pudo cargar el artículo'); // ✅ Añadido
                }
            } else { 
                setError('ID de artículo inválido.');
                toast.error('Error', 'ID de artículo inválido'); // ✅ Añadido
            }
            setLoading(false);
        };
        fetchArticle();
    }, [id]);

    const handleSubmit = async (formData: IArticle, imagesToDelete: string[]) => {
        try {
            let finalImageUrl = formData.imageUrl;
            if (!finalImageUrl) {
                if (formData.gallery.length > 0) {
                    finalImageUrl = formData.gallery[0].url;
                } else {
                    finalImageUrl = DEFAULT_PLACEHOLDER_IMAGE;
                }
            }

            const dataToSubmit = {
                ...formData,
                imageUrl: finalImageUrl,
                
                gallery: formData.gallery.map(image => ({
                    id: image.id || 0,
                    url: image.url,
                    caption: image.caption ?? '',
                    description: image.description ?? ''
                }))
            };

            await updateArticle(dataToSubmit as any); 

            if (imagesToDelete.length > 0) {
                await deleteImages(imagesToDelete);
            }
            hasSaved.current = true;
            toast.success('Éxito', 'Artículo actualizado con éxito'); // ✅ Añadido
            navigate('/articles'); 
        } catch (error: any) {
            toast.error('Error', `Error al actualizar el artículo: ${error.message}`); // ✅ Añadido
        }
    };

    if (loading) { return ( <AdminLayout pageTitle="Cargando..."><div className={styles.loadingMessage}>Cargando...</div></AdminLayout> ); }
    if (error) { return ( <AdminLayout pageTitle="Error"><div className={styles.loadingMessage} style={{ color: 'red' }}>Error: {error}</div></AdminLayout> ); }
    if (!article) { return ( <AdminLayout pageTitle="Error"><div className={styles.loadingMessage}>Artículo no encontrado.</div></AdminLayout> ); }

    return (
        <AdminLayout pageTitle={`Editar Artículo: ${article.title}`}>
            <div className={styles.formWrapper}>
                <div className={styles.pageHeader}>
                    <h2 className={styles.title}>{`Editar Artículo: ${article.title}`}</h2>
                    <Link to="/articles" className={styles.backButton}>← Volver</Link>
                </div>
                <ArticleForm 
                    initialData={article} 
                    onSubmit={handleSubmit} 
                    isEditing={true} 
                    sessionUploadsRef={sessionUploads}
                />
            </div>
        </AdminLayout>
    );
};

export default EditArticle;